import { makeAutoObservable, runInAction } from "mobx";
import type { MembershipProduct } from "../../platform_spaces/space/SpaceType";
import { getStandaloneSpaceId } from "../config";
import { StandaloneCatalogApi } from "./StandaloneCatalogApi";
import {
  STANDALONE_PRODUCT_SLOTS,
  type StandaloneSlotKey,
} from "./standaloneProductSlots";

export type StandaloneSlotStatus =
  | "loading"
  | "coming_soon"
  | "not_subscribed"
  | "ready";

export type StandaloneSlotView = {
  slotKey: StandaloneSlotKey;
  label: string;
  routePath: string;
  productKind: string;
  status: StandaloneSlotStatus;
  module: MembershipProduct | null;
};

function pickModuleForKind(
  modules: MembershipProduct[],
  productKind: string
): MembershipProduct | null {
  const matches = modules.filter((m) => m.productKind === productKind);
  if (matches.length === 0) {
    return null;
  }
  const subscribed = matches.find((m) => m.userSubscribed);
  return subscribed ?? matches[0];
}

export class StandaloneProductRegistry {
  loading = false;
  loadError: string | null = null;
  private modules: MembershipProduct[] = [];
  private loaded = false;

  constructor() {
    makeAutoObservable(this);
  }

  get isReady(): boolean {
    return this.loaded && !this.loading;
  }

  get catalogModules(): MembershipProduct[] {
    return this.modules;
  }

  slotViews(): StandaloneSlotView[] {
    return STANDALONE_PRODUCT_SLOTS.map((slot) => {
      const mod = pickModuleForKind(this.modules, slot.productKind);
      let status: StandaloneSlotStatus = "coming_soon";
      if (this.loading) {
        status = "loading";
      } else if (mod) {
        status = mod.userSubscribed ? "ready" : "not_subscribed";
      }
      return {
        slotKey: slot.slotKey,
        label: slot.label,
        routePath: slot.routePath,
        productKind: slot.productKind,
        status,
        module: mod,
      };
    });
  }

  viewForSlot(slotKey: StandaloneSlotKey): StandaloneSlotView | null {
    return this.slotViews().find((v) => v.slotKey === slotKey) ?? null;
  }

  async reload(): Promise<void> {
    this.loaded = false;
    this.loading = false;
    await this.ensureLoaded();
  }

  async ensureLoaded(): Promise<void> {
    if (this.loaded || this.loading) {
      return;
    }
    this.loading = true;
    this.loadError = null;
    const spaceId = getStandaloneSpaceId();
    try {
      const res = await StandaloneCatalogApi.loadCatalog(spaceId);
      runInAction(() => {
        if (typeof res === "string") {
          this.loadError = res;
          this.modules = [];
        } else {
          this.modules = res.modules;
        }
        this.loaded = true;
        this.loading = false;
      });
    } catch (e) {
      runInAction(() => {
        this.loadError = e instanceof Error ? e.message : "产品目录加载失败";
        this.modules = [];
        this.loaded = true;
        this.loading = false;
      });
    }
  }
}

export const standaloneProductRegistry = new StandaloneProductRegistry();
